import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Loader2, Plus, Save, ShieldAlert, Trash2 } from "lucide-react";
import {
  PROOF_MIN_MEDIA_PER_WEDDING, TESTIMONIALS_MAX, siteStatsSchema, siteTestimonialsSchema,
  type SiteStats, type SiteTestimonials, type Testimonial,
} from "@wedding/shared";
import { adminSiteApi, type AdminSiteData } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * "Satış & Site → Sosyal kanıt": gerçek kullanım sayılarının eşikleri +
 * çift yorumları. İki kart bağımsız kaydedilir (biri diğerinin taslağını ezmez).
 * Sayılar D1'den gelir; burada yalnız gösterim eşiği ayarlanır — sayı girilemez.
 */

const INPUT =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50";

type Props = {
  settings: AdminSiteData;
  onSaved: (patch: Partial<AdminSiteData>) => void;
  showToast: (msg: string, ok?: boolean) => void;
};

export function ProofTab({ settings, onSaved, showToast }: Props) {
  return (
    <div className="space-y-6">
      <StatsCard initial={settings.stats} raw={settings.proofRaw} onSaved={(stats) => onSaved({ stats })} showToast={showToast} />
      <TestimonialsCard initial={settings.testimonials} onSaved={(testimonials) => onSaved({ testimonials })} showToast={showToast} />
    </div>
  );
}

function SaveButton({ dirty, saving, onClick }: { dirty: boolean; saving: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!dirty || saving}
      className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
      {saving ? t("siteAdmin.saving") : t("siteAdmin.save")}
    </button>
  );
}

/** Ortak kaydet akışı: doğrula → PUT → yeni taban + toast. */
function useSectionSave<T>(section: "stats" | "testimonials", showToast: Props["showToast"]) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const save = async (value: T | null, done: (saved: T) => void) => {
    if (value === null) {
      showToast(t("siteAdmin.invalid"), false);
      return;
    }
    setSaving(true);
    try {
      // `value` şemadan geçti; bölüm adı ile eşleşen tip garanti.
      const res = await adminSiteApi.put(section, value as never);
      done((res.data as T | undefined) ?? value);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      showToast(t("siteAdmin.saveFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setSaving(false);
    }
  };
  return { saving, save };
}

// ─── Gerçek sayılar ───────────────────────────────────────────────────────

function StatsCard({ initial, raw, onSaved, showToast }: {
  initial: SiteStats;
  raw: AdminSiteData["proofRaw"];
  onSaved: (s: SiteStats) => void;
  showToast: Props["showToast"];
}) {
  const { t, i18n } = useTranslation();
  const nf = new Intl.NumberFormat(i18n.language);
  const [base, setBase] = useState(initial);
  const [show, setShow] = useState(initial.show);
  const [minWeddings, setMinWeddings] = useState(String(initial.minWeddings));
  const [minMedia, setMinMedia] = useState(String(initial.minMedia));
  const { saving, save } = useSectionSave<SiteStats>("stats", showToast);

  const parsed = siteStatsSchema.safeParse({
    show,
    minWeddings: Number(minWeddings.replace(/[.\s]/g, "")),
    minMedia: Number(minMedia.replace(/[.\s]/g, "")),
  });
  const value = parsed.success ? parsed.data : null;
  const dirty = show !== base.show || minWeddings !== String(base.minWeddings) || minMedia !== String(base.minMedia);

  const status = (n: number, min: number) =>
    !show ? t("siteAdmin.proof.off") : n >= min ? t("siteAdmin.proof.visible") : t("siteAdmin.proof.hidden");

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-muted/30 flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.proof.statsTitle")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.proof.statsDesc")}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="h-4 w-4 accent-primary cursor-pointer" />
          {t("siteAdmin.proof.show")}
        </label>
      </div>
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        {[
          { id: "weddings", label: t("siteAdmin.proof.minWeddings"), val: minWeddings, set: setMinWeddings, now: raw?.weddings, min: value?.minWeddings ?? base.minWeddings,
            current: raw ? t("siteAdmin.proof.currentWeddings", { n: nf.format(raw.weddings), min: PROOF_MIN_MEDIA_PER_WEDDING }) : null },
          { id: "media", label: t("siteAdmin.proof.minMedia"), val: minMedia, set: setMinMedia, now: raw?.media, min: value?.minMedia ?? base.minMedia,
            current: raw ? t("siteAdmin.proof.currentMedia", { n: nf.format(raw.media) }) : null },
        ].map((f) => (
          <div key={f.id} className="space-y-1.5">
            <label htmlFor={`proof-min-${f.id}`} className="text-xs font-medium text-foreground">{f.label}</label>
            <input
              id={`proof-min-${f.id}`}
              inputMode="numeric"
              value={f.val}
              onChange={(e) => f.set(e.target.value)}
              className={INPUT}
            />
            {f.current !== null && f.now !== undefined ? (
              <p className="text-[11px] text-muted-foreground" data-testid={`proof-now-${f.id}`}>
                {t("siteAdmin.proof.current")}: {f.current} —{" "}
                <span className={cn("font-semibold", show && f.now >= f.min ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400")}>
                  {status(f.now, f.min)}
                </span>
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">{t("siteAdmin.proof.unknown")}</p>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end px-5 pb-4">
        <SaveButton
          dirty={dirty}
          saving={saving}
          onClick={() =>
            void save(value, (saved) => {
              setBase(saved);
              setMinWeddings(String(saved.minWeddings));
              setMinMedia(String(saved.minMedia));
              setShow(saved.show);
              onSaved(saved);
            })
          }
        />
      </div>
    </div>
  );
}

// ─── Çift yorumları ───────────────────────────────────────────────────────

const newTestimonial = (): Testimonial => ({
  id: crypto.randomUUID(), couple: "", when: "", city: "", quote: "", rating: 5, visible: true,
});

function TestimonialsCard({ initial, onSaved, showToast }: {
  initial: SiteTestimonials;
  onSaved: (s: SiteTestimonials) => void;
  showToast: Props["showToast"];
}) {
  const { t } = useTranslation();
  const [base, setBase] = useState(initial);
  const [items, setItems] = useState<Testimonial[]>(initial.items);
  const { saving, save } = useSectionSave<SiteTestimonials>("testimonials", showToast);

  const parsed = siteTestimonialsSchema.safeParse({ items });
  const value = parsed.success ? parsed.data : null;
  const dirty = JSON.stringify(items) !== JSON.stringify(base.items);

  const patch = (id: string, p: Partial<Testimonial>) => setItems((list) => list.map((i) => (i.id === id ? { ...i, ...p } : i)));
  const move = (index: number, dir: -1 | 1) =>
    setItems((list) => {
      const next = [...list];
      const [it] = next.splice(index, 1);
      next.splice(index + dir, 0, it);
      return next;
    });

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-muted/30">
        <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.proof.testimonialsTitle")}</h2>
        <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-px" />
          {t("siteAdmin.proof.testimonialsDesc")}
        </p>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground">{t("siteAdmin.proof.empty")}</p>
      ) : (
        <ol className="divide-y divide-border">
          {items.map((item, index) => (
            <li key={item.id} data-testid={`testimonial-${index}`} className="px-5 py-4 space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <input aria-label={t("siteAdmin.proof.couple")} placeholder={t("siteAdmin.proof.couplePh")} maxLength={80}
                  value={item.couple} onChange={(e) => patch(item.id, { couple: e.target.value })} className={INPUT} />
                <input aria-label={t("siteAdmin.proof.when")} placeholder={t("siteAdmin.proof.whenPh")} maxLength={40}
                  value={item.when} onChange={(e) => patch(item.id, { when: e.target.value })} className={INPUT} />
                <input aria-label={t("siteAdmin.proof.city")} placeholder={t("siteAdmin.proof.cityPh")} maxLength={40}
                  value={item.city} onChange={(e) => patch(item.id, { city: e.target.value })} className={INPUT} />
              </div>
              <textarea aria-label={t("siteAdmin.proof.quote")} placeholder={t("siteAdmin.proof.quotePh")} rows={3} maxLength={400}
                value={item.quote} onChange={(e) => patch(item.id, { quote: e.target.value })} className={INPUT} />
              <div className="flex flex-wrap items-center gap-3">
                <select
                  aria-label={t("siteAdmin.proof.rating")}
                  value={item.rating ?? ""}
                  onChange={(e) => patch(item.id, { rating: e.target.value ? Number(e.target.value) : null })}
                  className="rounded-xl border border-border bg-background px-3 py-2 text-sm cursor-pointer"
                >
                  <option value="">{t("siteAdmin.proof.noRating")}</option>
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)}</option>)}
                </select>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={item.visible} onChange={(e) => patch(item.id, { visible: e.target.checked })}
                    className="h-4 w-4 accent-primary cursor-pointer" />
                  {t("siteAdmin.proof.showOnSite")}
                </label>
                <span className="ml-auto flex gap-1.5">
                  <IconButton label={t("siteAdmin.proof.moveUp")} disabled={index === 0} onClick={() => move(index, -1)}><ArrowUp className="h-4 w-4" /></IconButton>
                  <IconButton label={t("siteAdmin.proof.moveDown")} disabled={index === items.length - 1} onClick={() => move(index, 1)}><ArrowDown className="h-4 w-4" /></IconButton>
                  <IconButton label={t("siteAdmin.proof.remove")} danger onClick={() => setItems((list) => list.filter((i) => i.id !== item.id))}><Trash2 className="h-4 w-4" /></IconButton>
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-t border-border">
        <button
          type="button"
          onClick={() => setItems((list) => [...list, newTestimonial()])}
          disabled={items.length >= TESTIMONIALS_MAX}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm hover:bg-primary/5 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          {t("siteAdmin.proof.add")}
        </button>
        {items.length >= TESTIMONIALS_MAX && (
          <span className="text-xs text-muted-foreground">{t("siteAdmin.proof.limit", { max: TESTIMONIALS_MAX })}</span>
        )}
        <span className="ml-auto">
          <SaveButton
            dirty={dirty}
            saving={saving}
            onClick={() =>
              void save(value, (saved) => {
                setBase(saved);
                setItems(saved.items);
                onSaved(saved);
              })
            }
          />
        </span>
      </div>
    </div>
  );
}

function IconButton({ label, onClick, disabled, danger, children }: {
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-xl border p-2 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed",
        danger ? "border-red-500/30 text-red-500 hover:bg-red-500/5" : "border-border text-muted-foreground hover:text-foreground hover:bg-primary/5",
      )}
    >
      {children}
    </button>
  );
}
